import { useState } from 'react';
import MenuIcon from '@mui/icons-material/Menu';
import { Box, IconButton } from '@mui/material';
import PropTypes from 'prop-types';
import AppHeader from './AppHeader/AppHeader';
import AppSidebar from './AppSidebar/AppSidebar';
import { SidebarProvider } from '../context/SidebarProvider';
import { AuthorizationProvider } from '../../../authorization/useAuthorization';
import useAppShellSx from '../hooks/useAppShellSx';
import useLayoutMode from '../hooks/useLayoutMode';

const appLayoutRootSx = {
  flex: 1,
  minHeight: 0,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  overflow: 'hidden',
};

function AppLayoutShell({ children }) {
  const sx = useAppShellSx();
  const { hideSidebar, hideHeader, lockContentScroll } = useLayoutMode();
  const [mobileOpen, setMobileOpen] = useState(false);

  const contentSx = [
    sx.contentRegion,
    lockContentScroll ? sx.contentRegionLocked : sx.contentRegionScroll,
  ];

  const scrollableMain = (
    <Box component="main" sx={contentSx}>
      {children}
    </Box>
  );

  const mobileMenuButton = !hideSidebar ? (
    <IconButton
      onClick={() => setMobileOpen(true)}
      aria-label="Open navigation menu"
      sx={{
        ...sx.mobileMenuButton,
        ...(hideHeader ? sx.mobileMenuButtonNoHeader : {}),
      }}
    >
      <MenuIcon />
    </IconButton>
  ) : null;

  const sidebars = !hideSidebar ? (
    <AppSidebar
      mobileOpen={mobileOpen}
      onMobileClose={() => setMobileOpen(false)}
    />
  ) : null;

  if (hideHeader) {
    return (
      <Box sx={sx.layoutShell}>
        {sidebars}
        <Box sx={sx.mainColumnNoHeader}>
          {mobileMenuButton}
          {scrollableMain}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={sx.layoutShell}>
      {sidebars}

      <Box sx={sx.mainColumn}>
        {mobileMenuButton}
        <AppHeader />
        {scrollableMain}
      </Box>
    </Box>
  );
}

AppLayoutShell.propTypes = {
  children: PropTypes.node,
};

export default function AppLayout({ children }) {
  return (
    <AuthorizationProvider>
      <SidebarProvider>
        <Box sx={appLayoutRootSx}>
          <AppLayoutShell>{children}</AppLayoutShell>
        </Box>
      </SidebarProvider>
    </AuthorizationProvider>
  );
}

AppLayout.propTypes = {
  children: PropTypes.node,
};
